import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  useTranslation,
} from 'react-i18next';

import AppIcon
  from '../ui/AppIcon';

import LanguageSelector
  from '../ui/LanguageSelector';

import LoginModal
  from '../../features/auth/components/LoginModal';

import {
  useAuth,
} from '../../features/auth/AuthContext';

import {
  ROUTES,
} from '../../config/routes';

import './AppHeader.css';

export default function AppHeader({
  items = [],
  pageTitle = '',
  pageDescription = '',
  onBrandClick,
}) {
  const isPageHeader = Boolean(pageTitle);
  const {
    t,
  } = useTranslation(
    'app',
  );

  const location =
    useLocation();

  const navigate =
    useNavigate();

  const {
    user,
    signOut,
  } = useAuth();

  const headerRef =
    useRef(null);

  const [
    isMobileMenuOpen,
    setIsMobileMenuOpen,
  ] = useState(false);

  const [
    isLoginModalOpen,
    setIsLoginModalOpen,
  ] = useState(false);

  const [
    activeSectionId,
    setActiveSectionId,
  ] = useState('');

  const closeMenus =
    () => {
      setIsMobileMenuOpen(
        false,
      );
    };

  const closeLoginModal =
    () => {
      setIsLoginModalOpen(
        false,
      );
    };

  const handleLogoClick =
    () => {
      closeMenus();

      if (onBrandClick) {
        onBrandClick();
        return;
      }

      navigate(
        ROUTES.root,
      );

      if (location.pathname !== ROUTES.root) {
        return;
      }

      window.requestAnimationFrame(
        () => {
          window.scrollTo({
            top: 0,
            behavior:
              'smooth',
          });
        },
      );
    };

  const handleSectionNavigation =
    (
      sectionId,
    ) => {
      closeMenus();

      if (
        !sectionId
      ) {
        return;
      }

      const hash =
        `#${sectionId}`;

      if (
        location.pathname ===
        ROUTES.root
      ) {
        navigate({
          pathname:
            ROUTES.root,
          hash,
        });

        window.requestAnimationFrame(
          () => {
            const target =
              document.getElementById(
                sectionId,
              );

            if (
              target
            ) {
              target.scrollIntoView({
                behavior:
                  'smooth',
                block:
                  'start',
              });
            }
          },
        );

        return;
      }

      navigate({
        pathname:
          ROUTES.root,
        hash,
      });
    };

  const handleLogin =
    () => {
      closeMenus();

      setIsLoginModalOpen(
        true,
      );
    };

  const handleLogout =
    async () => {
      closeMenus();

      navigate(
        ROUTES.root,
        {
          replace:
            true,
        },
      );

      try {
        if (
          typeof signOut ===
          'function'
        ) {
          await signOut();
        }
      } catch (error) {
        if (
          process.env.NODE_ENV !==
          'production'
        ) {
          console.error(
            '[Header] Logout failed:',
            error,
          );
        }
      }
    };

  useEffect(
    () => {
      closeMenus();

      setIsLoginModalOpen(
        false,
      );
    },
    [
      location.pathname,
      location.hash,
    ],
  );

  useEffect(
    () => {
      const handleMouseDown =
        (
          event,
        ) => {
          if (
            headerRef.current &&
            !headerRef.current.contains(
              event.target,
            )
          ) {
            closeMenus();
          }
        };

      document.addEventListener(
        'mousedown',
        handleMouseDown,
      );

      return () => {
        document.removeEventListener(
          'mousedown',
          handleMouseDown,
        );
      };
    },
    [],
  );

  useEffect(
    () => {
      if (
        location.pathname !==
        ROUTES.root
      ) {
        setActiveSectionId(
          '',
        );

        return undefined;
      }

      let animationFrameId =
        null;

      const updateActiveSection =
        () => {
          const headerHeight =
            headerRef.current
              ?.offsetHeight ||
            0;

          const triggerPosition =
            headerHeight +
            window.innerHeight *
              0.22;

          let nextActiveSection =
            '';

          items.forEach(
            (
              menuItem,
            ) => {
              const section =
                document.getElementById(
                  menuItem.homeSection,
                );

              if (
                !section
              ) {
                return;
              }

              const rect =
                section.getBoundingClientRect();

              if (
                rect.top <=
                  triggerPosition &&
                rect.bottom >
                  triggerPosition
              ) {
                nextActiveSection =
                  menuItem.homeSection;
              }
            },
          );

          if (
            !nextActiveSection
          ) {
            const availableSections =
              items
                .map(
                  (
                    menuItem,
                  ) => {
                    const section =
                      document.getElementById(
                        menuItem.homeSection,
                      );

                    if (
                      !section
                    ) {
                      return null;
                    }

                    return {
                      id:
                        menuItem.homeSection,
                      rect:
                        section.getBoundingClientRect(),
                    };
                  },
                )
                .filter(
                  Boolean,
                );

            const passedSections =
              availableSections.filter(
                (
                  section,
                ) =>
                  section.rect.top <=
                  triggerPosition,
              );

            if (
              passedSections.length >
              0
            ) {
              nextActiveSection =
                passedSections[
                  passedSections.length -
                    1
                ].id;
            }
          }

          setActiveSectionId(
            nextActiveSection,
          );

          animationFrameId =
            null;
        };

      const handleScroll =
        () => {
          if (
            animationFrameId !==
            null
          ) {
            return;
          }

          animationFrameId =
            window.requestAnimationFrame(
              updateActiveSection,
            );
        };

      updateActiveSection();

      window.addEventListener(
        'scroll',
        handleScroll,
        {
          passive: true,
        },
      );

      window.addEventListener(
        'resize',
        handleScroll,
      );

      return () => {
        window.removeEventListener(
          'scroll',
          handleScroll,
        );

        window.removeEventListener(
          'resize',
          handleScroll,
        );

        if (
          animationFrameId !==
          null
        ) {
          window.cancelAnimationFrame(
            animationFrameId,
          );
        }
      };
    },
    [
      location.pathname,
      items,
    ],
  );

  return (
    <>
      <header
        ref={
          headerRef
        }
        className="app-header is-home"
      >
        <div
          className="app-header-left"
        >
          <button
            type="button"
            className="app-header-brand"
            onClick={handleLogoClick}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/Hanwha_logo.png`}
              alt="Hanwha"
            />

            <span>
              <strong>
                Hanwha Semitech
              </strong>

              <strong>
                Hi-Portal
              </strong>
            </span>
          </button>

          <span
            className="app-header-divider"
          />

          {isPageHeader ? (
            <div className="app-header-page-title">
              <strong>{pageTitle}</strong>
              {pageDescription && <span>{pageDescription}</span>}
            </div>
          ) : (
            <nav
              className="app-header-navigation"
            >
              {items.map(
              (
                menuItem,
              ) => {
                const active =
                  location.pathname ===
                    ROUTES.root &&
                  activeSectionId ===
                    menuItem.homeSection;

                return (
                  <button
                    key={
                      menuItem.id
                    }
                    type="button"
                    className={[
                      'app-header-navigation-item',
                      active
                        ? 'is-active'
                        : '',
                    ]
                      .filter(
                        Boolean,
                      )
                      .join(
                        ' ',
                      )}
                    onClick={() =>
                      handleSectionNavigation(
                        menuItem.homeSection,
                      )
                    }
                  >
                    {t(
                      menuItem.translationKey,
                    )}
                  </button>
                );
              },
              )}
            </nav>
          )}
        </div>

        <div
          className="app-header-right"
        >
<div
            className="app-header-language"
          >
            <LanguageSelector />
          </div>

          {!isPageHeader && (user ? (
            <>
              <button
                type="button"
                className="app-header-logout"
                onClick={
                  handleLogout
                }
              >
                <AppIcon
                  type="logout"
                  size={17}
                />

                <span>
                  {t(
                    'navigation.logout',
                  )}
                </span>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="app-header-logout"
              onClick={
                handleLogin
              }
            >
              <AppIcon
                type="user"
                size={17}
              />

              <span>
                {t(
                  'navigation.login',
                )}
              </span>
            </button>
          ))}

          {!isPageHeader && <button
            type="button"
            className="app-header-mobile-menu-button"
            onClick={() =>
              setIsMobileMenuOpen(
                (
                  previousValue,
                ) =>
                  !previousValue,
              )
            }
            aria-expanded={
              isMobileMenuOpen
            }
          >
            <AppIcon
              type="menu"
              size={21}
            />
          </button>}

          {!isPageHeader && isMobileMenuOpen && (
            <div
              className="app-header-mobile-menu"
            >
              {items.map(
                (
                  menuItem,
                ) => {
                  const active =
                    location.pathname ===
                      ROUTES.root &&
                    activeSectionId ===
                      menuItem.homeSection;

                  return (
                    <button
                      key={
                        menuItem.id
                      }
                      type="button"
                      className={[
                        'app-header-mobile-item',
                        active
                          ? 'is-active'
                          : '',
                      ]
                        .filter(
                          Boolean,
                        )
                        .join(
                          ' ',
                        )}
                      onClick={() =>
                        handleSectionNavigation(
                          menuItem.homeSection,
                        )
                      }
                    >
                      {t(
                        menuItem.translationKey,
                      )}
                    </button>
                  );
                },
              )}
            </div>
          )}
        </div>
      </header>

      {!isPageHeader && <LoginModal
        open={
          isLoginModalOpen
        }
        onClose={
          closeLoginModal
        }
      />}
    </>
  );
}
